import { useQuery } from "@tanstack/react-query"
import { Row, Col, Card, CardBody, Table, Badge, Spinner } from "reactstrap"

import apiClient from "api/axios"
import { formatBDT } from "utils/formatPrice"
import { formatReadableDate } from "utils/formatDate"

const fetchLedger = async id => {
  const { data } = await apiClient.get(`/customers/${id}/ledger`)
  return data
}

const StatCard = ({ label, value, color }) => (
  <Col md="4" className="mb-3">
    <Card className="border-0 shadow-sm h-100">
      <CardBody className="text-center">
        <div className="text-muted small">{label}</div>
        <div className={`fs-4 fw-bold text-${color}`}>{formatBDT(value)}</div>
      </CardBody>
    </Card>
  </Col>
)

const CustomerWalletTab = ({ customer }) => {
  const customerId = customer?.id

  const { data, isLoading } = useQuery({
    queryKey: ["customer-ledger", customerId],
    queryFn: () => fetchLedger(customerId),
    enabled: Boolean(customerId),
  })

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner />
      </div>
    )
  }

  const ledger = data?.data || {}
  const serviceInvoices = ledger.service_invoices || []
  const moneyReceipts = ledger.money_receipts || []
  const payments = ledger.payments || []
  const moneyReceiptPayments = ledger.money_receipt_payments || []

  return (
    <div>
      <h6 className="fw-semibold mb-2 text-muted">Car Care Wallet</h6>
      <Row>
        <StatCard
          label="Total Invoiced"
          value={ledger.total_invoiced || 0}
          color="info"
        />
        <StatCard
          label="Total Paid"
          value={ledger.total_paid || 0}
          color="success"
        />
        <StatCard
          label="Total Due"
          value={ledger.total_due || 0}
          color={ledger.total_due > 0 ? "danger" : "success"}
        />
      </Row>

      <h6 className="fw-semibold mb-2 mt-2 text-muted">Car Sales Wallet</h6>
      <Row>
        <StatCard
          label="Total Amount"
          value={ledger.mr_total_amount || 0}
          color="info"
        />
        <StatCard
          label="Total Paid"
          value={ledger.mr_total_paid || 0}
          color="success"
        />
        <StatCard
          label="Total Due"
          value={ledger.mr_total_due || 0}
          color={ledger.mr_total_due > 0 ? "danger" : "success"}
        />
      </Row>

      <Card className="border-0 shadow-sm mb-4">
        <CardBody className="p-3">
          <h6 className="fw-semibold mb-3">
            <i className="bx bx-car me-2" />
            Service Invoices
          </h6>
          {serviceInvoices.length ? (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Invoice No</th>
                    <th>Date</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceInvoices.map(inv => (
                    <tr key={inv.id}>
                      <td className="fw-semibold">{inv.invoice_no || "-"}</td>
                      <td className="text-muted">
                        {inv.invoice_date
                          ? formatReadableDate(inv.invoice_date)
                          : "-"}
                      </td>
                      <td className="text-end">
                        {formatBDT(inv.total_amount)}
                      </td>
                      <td className="text-end">
                        <Badge
                          className={`badge-soft-${
                            Number(inv.current_due) > 0 ? "danger" : "success"
                          }`}
                        >
                          {formatBDT(inv.current_due)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-muted small">No service invoices.</div>
          )}
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm mb-4">
        <CardBody className="p-3">
          <h6 className="fw-semibold mb-3">
            <i className="bx bx-receipt me-2" />
            Car Money Receipts
          </h6>
          {moneyReceipts.length ? (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>MR No</th>
                    <th>Issued</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Paid</th>
                    <th className="text-end">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {moneyReceipts.map(mr => (
                    <tr key={mr.id}>
                      <td className="fw-semibold">{mr.mr_no || "-"}</td>
                      <td className="text-muted">
                        {mr.issued_date
                          ? formatReadableDate(mr.issued_date)
                          : "-"}
                      </td>
                      <td className="text-end">
                        {formatBDT(mr.total_amount)}
                      </td>
                      <td className="text-end">{formatBDT(mr.amount_paid)}</td>
                      <td className="text-end">
                        <Badge
                          className={`badge-soft-${
                            Number(mr.due_amount) > 0 ? "danger" : "success"
                          }`}
                        >
                          {formatBDT(mr.due_amount)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-muted small">No money receipts.</div>
          )}
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm mb-4">
        <CardBody className="p-3">
          <h6 className="fw-semibold mb-3">
            <i className="bx bx-money me-2" />
            Car Care Payments
          </h6>
          {payments.length ? (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id}>
                      <td className="text-muted">
                        {p.received_date
                          ? formatReadableDate(p.received_date)
                          : "-"}
                      </td>
                      <td>{p.payment_method?.name || "-"}</td>
                      <td>{p.reference_number || "-"}</td>
                      <td className="text-end">{formatBDT(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-muted small">No payments recorded.</div>
          )}
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardBody className="p-3">
          <h6 className="fw-semibold mb-3">
            <i className="bx bx-receipt me-2" />
            Money Receipt Payments
          </h6>
          {moneyReceiptPayments.length ? (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>MR No</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {moneyReceiptPayments.map(p => (
                    <tr key={p.id}>
                      <td className="text-muted">
                        {p.received_date
                          ? formatReadableDate(p.received_date)
                          : "-"}
                      </td>
                      <td>{p.money_receipt?.mr_no || "-"}</td>
                      <td>{p.payment_method?.name || "-"}</td>
                      <td>{p.reference_number || "-"}</td>
                      <td
                        className={`text-end ${
                          Number(p.amount) < 0 ? "text-danger" : ""
                        }`}
                      >
                        {formatBDT(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-muted small">
              No money receipt payments recorded.
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default CustomerWalletTab
